import { DimensionType } from "@models/IDimension";
import useContextUrl from "./useContextUrl";
import {
  getDarwinApiUrlFromLocalStorage,
  getUrlWithoutTrailingSlash,
} from "@context/usePublicConfigStore";

interface BaseParams {
  contextUrl: string;
  baseUrl: string;
}

const routesConfig = {
  advancedSearch: "/editor/advanceSearch/",
  editor: "/scheduler/editor/",
  bundle: "/editor/vacancies/",
  searchResults: "/scheduler/editor/searchBy/",
  changeHistory: "/editor/groups-manager/change_history/",
  linkChangeHistory: "/editor/link/change-history/",
  packagesAndGroupsReport: "/report/packages-and-groups/",
  microprogrammingGroupsReport: "/report/microprogramming-groups/",
  groupsManager: "/editor/groups-manager/",
  createSection: "/editor/create-section/",
  createPackage: "/editor/create-package/",
  editAssignment: "/editor/form-edit/",
  bookingList: "/booking/list/",
  home: "/home/",
  darwinUrl: () => getDarwinApiUrlFromLocalStorage().darwinLegacyUrl,
  holidays: "/editor/session/holidays/",
  catalogs: "/catalogs",
  reports: "/reports",
  programmingStructures: "/programming-structures",
};

export const getDynamicHomeUrl = (workspaceId: string, scenarioId: string, originId?: string) => {
  return `${routesConfig.home}${workspaceId}/${scenarioId}${!!originId ? `/${originId}` : ""}`;
};

const getAdvancedSearchUrl = ({
  contextUrl,
  baseUrl,
  dimension,
}: BaseParams & {
  dimension?: DimensionType;
}) => {
  if (dimension) {
    return `${baseUrl}${dimension}/${contextUrl}`;
  }

  return baseUrl + contextUrl;
};

/**
 * Custom hook that generates URLs for navigation within the application based on the current context.
 *
 * @returns An object containing functions to generate URLs for advanced search, editor, and bundle pages.
 */
const useNavigationUrl = () => {
  const { scenario, origin, workspace } = useContextUrl();
  const contextUrl = `${workspace}/${scenario}/${origin}`;

  const adaptedDarwinUrl = routesConfig.darwinUrl
    ? getUrlWithoutTrailingSlash(routesConfig.darwinUrl())
    : "";

  return {
    baseRoutes: routesConfig,
    adaptedDarwinUrl,
    advancedSearchUrl: (dimension?: DimensionType) =>
      getAdvancedSearchUrl({ contextUrl, baseUrl: routesConfig.advancedSearch, dimension }),
    editorUrl: (dimension: DimensionType, id: string) =>
      `${routesConfig.editor}${dimension}/${contextUrl}/${id}`,
    bundleUrl: (bundleId: string) => `${routesConfig.bundle}${contextUrl}/${bundleId}`,
    advancedSearchResultsUrl: (dimension: DimensionType, id: string) =>
      `${routesConfig.searchResults}${dimension}/${contextUrl}/?advance=${id}`,
    advancedSearchWithValueSelectUrl: (valueSelect: string) =>
      `${routesConfig.advancedSearch}${valueSelect}/${contextUrl}`,
    changeHistoryUrl: () => `${routesConfig.changeHistory}${contextUrl}`,
    linkChangeHistoryUrl: () => `${routesConfig.linkChangeHistory}${contextUrl}`,
    packagesAndGroupsReportUrl: () => `${routesConfig.packagesAndGroupsReport}${contextUrl}`,
    microprogrammingGroupsReportUrl: () =>
      `${routesConfig.microprogrammingGroupsReport}${contextUrl}`,
    groupsManagerUrl: () => `${routesConfig.groupsManager}${contextUrl}`,
    groupDetailUrl: (groupId: string) => `${routesConfig.groupsManager}${contextUrl}/${groupId}`,
    createSectionUrl: () => `${routesConfig.createSection}${contextUrl}`,
    createPackageUrl: () => `${routesConfig.createPackage}${contextUrl}`,
    editAssignmentUrl: (linkId: string) => `${routesConfig.editAssignment}${contextUrl}/${linkId}`,
    bookingListUrl: () => `${routesConfig.bookingList}${contextUrl}`,
    adaptedHomeUrl: () => {
      return getDynamicHomeUrl(workspace, scenario, origin);
    },
    homeUrl: (workspaceId: string, scenarioId: string, originId: string) =>
      `${routesConfig.home}${workspaceId}/${scenarioId}/${originId}`,
    getPackageClashReportUrl: () =>
      `${adaptedDarwinUrl}/resultados/report_package_clashes_edh/${origin}?scenario_id=${scenario}`,
    getGroupsAndPackagesReportUrl: (filters?: {
      campus?: string;
      modality?: string;
      program?: string;
    }) => {
      const campusFilter = filters?.campus ? `&campus=${filters.campus}` : "";
      const modalityFilter = filters?.modality ? `&modality=${filters.modality}` : "";
      const programFilter = filters?.program ? `&program=${filters.program}` : "";

      return `${adaptedDarwinUrl}/groups_manager/group_packages/${origin}?scenario_id=${scenario}${programFilter}${campusFilter}${modalityFilter}`;
    },
    getMicroprogrammingGroupsReportUrl: ({ term }: { term: string }) =>
      `${adaptedDarwinUrl}/resultados/report_microprogramming_groups/${origin}?scenario_id=${scenario}&term_id=${term}`,
    getDarwinUrl: () => `${routesConfig.darwinUrl()}`,
    holidaysUrl: () => `${routesConfig.holidays}${contextUrl}`,
    getDeletedSessionsByHolidayReportUrl: () =>
      `${adaptedDarwinUrl}/resultados/deleted_sessions_by_holiday_report/${origin}?scenario_id=${scenario}`,
    getLinkLogEntriesReportUrl: (token: string, userId?: string) => {
      const userIdParam = userId ? `&user_id=${userId}` : "";
      return `${adaptedDarwinUrl}/resultados/link_log_entries_report/${origin}?token=${token}&scenario_id=${scenario}&origin_id=${origin}${userIdParam}`;
    },
    catalogsUrl: () =>
      `${routesConfig.catalogs}/${workspace}/${scenario}${origin ? `/${origin}` : ""}`,
    reportsUrl: () =>
      `${routesConfig.reports}/${workspace}/${scenario}${origin ? `/${origin}` : ""}`,
    groupRestrictionsUrl: (groupId: string) =>
      `${routesConfig.editor}group/restrictions/${contextUrl}/${groupId}`,
    getProgrammingStructuresUrl: () => `${routesConfig.programmingStructures}/list/${contextUrl}`,
    getProgrammingStructuresByIdUrl: (id: string, action: "new" | "update" | "details") =>
      `${routesConfig.programmingStructures}/${action}/${contextUrl}/${id}`,
  };
};

export default useNavigationUrl;
